import { apiRequest } from './api.js';
import firebaseService from './firebaseService.js';

// Voice API Service for managing voice files
export const voiceApiService = {
  // Upload voice file for a specific proverb
  async uploadVoiceFile(proverbId, audioFile, language = 'chinese') {
    try {
      console.log(`🎤 Uploading voice file for proverb ${proverbId}...`);
      
      // Check if user is authenticated before upload
      const authToken = localStorage.getItem('token');
      
      if (!authToken) {
        console.error('❌ No auth token found in localStorage');
        console.error('Available keys:', Object.keys(localStorage));
        throw new Error('No authentication token found. Please log in again.');
      }
      
      console.log('🔐 Auth token found:', authToken.substring(0, 20) + '...');
      
      const formData = new FormData();
      formData.append('audio', audioFile);
      formData.append('proverb_id', proverbId);
      formData.append('language', language);
      
      console.log('📤 FormData prepared:', {
        audioFileName: audioFile.name,
        audioSize: audioFile.size,
        audioType: audioFile.type,
        proverb_id: proverbId,
        language: language
      });
      
      const response = await apiRequest('/voices/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header, let browser set it for FormData
        // Don't pass empty headers object - it overrides Authorization header
        skipContentType: true
      });
      
      console.log('✅ Voice file uploaded successfully:', response.id);
      
      // Log activity
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const isUpdate = response.isUpdate || false;
        
        // Get proverb details to get the numeric proverbId
        let numericProverbId = proverbId; // fallback to the passed proverbId (could be Firebase ID)
        try {
          const proverbDetails = await firebaseService.proverbs.getProverbById(proverbId);
          numericProverbId = proverbDetails.proverbId || proverbId;
        } catch (error) {
          console.warn('Could not fetch proverb details for activity logging:', error);
        }
        
        await firebaseService.utils.logUserActivity(
          user.id || 'unknown',
          user.name || user.email || 'Unknown User',
          isUpdate ? 'voice_updated' : 'voice_created',
          {
            proverbId: numericProverbId,
            language: language,
            fileName: audioFile.name,
            fileSize: audioFile.size,
            fileType: audioFile.type,
            voiceId: response.voice?.id || response.id,
            action: isUpdate ? 'Updated voice file' : 'Created new voice file',
            description: isUpdate 
              ? `Updated voice recording for proverb #${numericProverbId}` 
              : `Added voice recording for proverb #${numericProverbId}`
          }
        );
        console.log('✅ Voice activity logged to Firebase');
      } catch (activityError) {
        console.error('⚠️ Failed to log voice activity:', activityError);
        // Don't throw error for activity logging failure
      }
      
      return response;
    } catch (error) {
      console.error('❌ Failed to upload voice file:', error);
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // Clear invalid auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        throw new Error('Authentication expired. Please log in again.');
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        throw new Error('Permission denied. Admin or Super Admin role required.');
      } else if (error.message.includes('413')) {
        throw new Error('File too large. Please select a smaller audio file.');
      } else if (error.message.includes('400')) {
        throw new Error('Invalid file format. Please upload an MP3 file.');
      }
      
      throw new Error(`Failed to upload voice file: ${error.message}`);
    }
  },

  // Get voice file for a specific proverb
  async getVoiceByProverbId(proverbId, language = 'chinese') {
    try {
      // First try to get by proverb_id
      const params = new URLSearchParams({
        proverb_id: proverbId.toString(),
        language
      });
      
      let response = await apiRequest(`/voices?${params}`);
      
      // If no voices found with proverb_id, try searching by title pattern
      if (!response.voices || response.voices.length === 0) {
        const searchParams = new URLSearchParams({
          search: `Proverb ${proverbId}`,
          language
        });
        
        response = await apiRequest(`/voices?${searchParams}`);
        
        // Filter to find exact match for this proverb
        if (response.voices && response.voices.length > 0) {
          const exactMatch = response.voices.find(voice => 
            voice.title?.includes(`Proverb ${proverbId}`) || 
            voice.description?.includes(`proverb ID ${proverbId}`)
          );
          
          if (exactMatch) {
            return exactMatch;
          }
        }
      }
      
      // Return the first voice if found, otherwise null
      return response.voices && response.voices.length > 0 ? response.voices[0] : null;
    } catch (error) {
      console.error('❌ Failed to fetch voice file:', error);
      // Don't throw error for missing voice - it's normal
      return null;
    }
  },

  // Get all voices with optional filters
  async getAllVoices(page = 1, limit = 50, language = '') {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(language && { language })
      });

      const response = await apiRequest(`/voices?${params}`);
      return response;
    } catch (error) {
      console.error('❌ Failed to fetch voices:', error);
      throw new Error(`Failed to load voices: ${error.message}`);
    }
  },

  // Delete a voice file
  async deleteVoice(voiceId) {
    try {
      console.log(`🗑️ Deleting voice ${voiceId}...`);
      
      const response = await apiRequest(`/voices/${voiceId}`, {
        method: 'DELETE'
      });

      console.log('✅ Voice deleted successfully');
      
      // Log activity
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        await firebaseService.utils.logUserActivity(
          user.id || 'unknown',
          user.name || user.email || 'Unknown User',
          'voice_deleted',
          {
            voiceId: voiceId,
            action: 'Deleted voice file',
            description: `Deleted voice recording (ID: ${voiceId})`
          }
        );
        console.log('✅ Voice deletion activity logged to Firebase');
      } catch (activityError) {
        console.error('⚠️ Failed to log voice deletion activity:', activityError);
        // Don't throw error for activity logging failure
      }
      
      return response;
    } catch (error) {
      console.error('❌ Failed to delete voice:', error);
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        throw new Error('Permission denied. Admin or Super Admin role required.');
      } else if (error.message.includes('404')) {
        throw new Error('Voice file not found.');
      }
      
      throw new Error(`Failed to delete voice: ${error.message}`);
    }
  },

  // Update voice metadata
  async updateVoice(voiceId, updateData) {
    try {
      console.log(`📝 Updating voice ${voiceId}...`);
      
      const response = await apiRequest(`/voices/${voiceId}`, {
        method: 'PATCH',
        body: JSON.stringify(updateData)
      });

      console.log('✅ Voice updated successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to update voice:', error);
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        throw new Error('Permission denied. Admin or Super Admin role required.');
      } else if (error.message.includes('404')) {
        throw new Error('Voice file not found.');
      }
      
      throw new Error(`Failed to update voice: ${error.message}`);
    }
  }
};

export default voiceApiService;
